import { CURRENCY_CONFIG, DATE_FORMAT_OPTIONS } from './constants.ts';

/**
 * Format a number as currency
 */
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat(CURRENCY_CONFIG.locale, {
        style: 'currency',
        currency: CURRENCY_CONFIG.currency,
        minimumFractionDigits: CURRENCY_CONFIG.minimumFractionDigits,
        maximumFractionDigits: CURRENCY_CONFIG.maximumFractionDigits,
    }).format(amount);
};

/**
 * Format a date string to localized date and time
 */
export const formatDateTime = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid date';
        }
        return date.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS.dateTime);
    } catch (error) {
        return 'Invalid date';
    }
};

/**
 * Format a date string to localized date only
 */
export const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid date';
        }
        return date.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS.date);
    } catch (error) {
        return 'Invalid date';
    }
};

/**
 * Format a date string to localized time only
 */
export const formatTime = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid time';
        }
        return date.toLocaleTimeString('en-US', DATE_FORMAT_OPTIONS.time);
    } catch (error) {
        return 'Invalid time';
    }
};

/**
 * Format duration in minutes to human readable format
 */
export const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');

    // Check if it's a US number
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // Check if it's a US number with country code
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    // Return as is if it doesn't match common patterns
    return phone;
};

/**
 * Format license plate for display
 */
export const formatLicensePlate = (plate: string): string => {
    return plate.toUpperCase().replace(/\s+/g, ' ').trim();
};

/**
 * Format credit card number (mask all but last 4 digits)
 */
export const formatCreditCard = (lastFour: string, brand?: string): string => {
    const brandPrefix = brand ? `${brand.toUpperCase()} ` : '';
    return `${brandPrefix}**** **** **** ${lastFour}`;
};

/**
 * Format file size in bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Capitalize first letter of each word
 */
export const formatTitle = (text: string): string => {
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 30) return `${diffInDays}d ago`;

        const diffInMonths = Math.floor(diffInDays / 30);
        if (diffInMonths < 12) return `${diffInMonths}mo ago`;

        const diffInYears = Math.floor(diffInMonths / 12);
        return `${diffInYears}y ago`;
    } catch (error) {
        return 'Unknown';
    }
};

export const formatIsActive = (status: boolean): string => {
    return status ? 'Active' : 'Inactive';
}

export const formatIsBlocked = (status: boolean): string => {
    return status ? 'Blocked' : 'Unblocked';
}

export const formatIsPayed = (status: boolean): string => {
    return status ? 'Paid' : 'Unpaid';
}

export const formatIsCanceled = (status: boolean): string => {
    return status ? 'isCancelled' : 'Not cancelled';
}

export const formatIsConfirmed = (status: boolean): string => {
    return status ? 'Confirmed' : 'not_confirmed';
}

export const formatIsSentToExec = (status: boolean): string => {
    return status ? 'Yes' : 'No';
}

export const snakeToHuman = (str: string): string => {
    return str
        .replace(/_/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
}

type AddressHash = {
    city: string | null;
    district: string | null;
    street: string | null;
    car_number: string | null;
    parking_operator: string | null;
    identifier: string | null;
    id: string | null;
};

export const parseAddress = (input: string): AddressHash => {
    if(input === null){
        return {
            city: null,
            district: null,
            street: null,
            car_number: null,
            parking_operator: null,
            identifier: null,
            id: null
        }
    }
    const [city, district, street, car_number, parking_operator, identifier, id] = input
        .split(',')
        .map(s => s.trim());

    return { city, district, street, car_number, parking_operator, identifier, id };
}

export const isNowInRange = (start_date: string, expire_date: string): boolean => {
    const now = new Date();
    const start = new Date(start_date);
    const end = new Date(expire_date);

    return now >= start && now <= end;
}
